import { getConditionPrefix } from "src/global/config.ts"
import { ParamTypes } from "src/pages/DataAssets/views/Expectations/Expectation/Expectation"
import {
  FORECAST_ANOMALY_TEMPLATE,
  VOLUME_ANOMALY_DYNAMIC_TEMPLATE,
} from "src/pages/DataAssets/views/Expectations/Expectation/constants"
import { detectParameterSource } from "src/pages/DataAssets/views/Expectations/Expectation/utils/detectParameterSource"

type TemplateResolverFn = (template: string, params: ParamTypes, expectationType: string) => string

/**
 * Type guard to check if parameters match volume anomaly detection (Dynamic Parameters, not forecast)
 * for "Must have a row count greater than the row count of the last run."
 */
function isVolumeAnomalyDetectionDynamicParams(params: ParamTypes): boolean {
  // Parse offset value - handle both "0" and "-0.00%" formats
  const offsetValue = params.offset?.value
  if (Array.isArray(offsetValue)) return false

  const parsedOffset =
    typeof offsetValue === "string" ? parseFloat(offsetValue.replace("%", "")) : parseFloat(String(offsetValue))

  // Parse range value
  const rangeValue = params.range?.value
  const parsedRange = typeof rangeValue === "string" ? parseFloat(rangeValue) : parseFloat(String(rangeValue))

  return (
    parsedOffset === 0 &&
    parsedRange === 1 &&
    params.constraint_fn?.value === "mean" &&
    params.strict_min?.value === true
  )
}

export const tableRowCountToBeBetweenResolver: TemplateResolverFn = (template: string, params: ParamTypes): string => {
  const parameterSource = detectParameterSource(params)

  // Forecast parameters - return anomaly detection message
  if (parameterSource === "forecast") {
    return FORECAST_ANOMALY_TEMPLATE
  }

  // Dynamic Parameters with specific conditions
  if (parameterSource === "dynamic") {
    // Special case for volume anomaly detection with Dynamic Parameters
    if (isVolumeAnomalyDetectionDynamicParams(params)) {
      return VOLUME_ANOMALY_DYNAMIC_TEMPLATE
    }

    // Other Dynamic Parameters
    // The offset should never be an array for this expectation
    if (Array.isArray(params.offset?.value)) {
      return template
    }

    return `${getConditionPrefix(template)} Must have $offset of the $constraint_fn row counts of the last $range runs`
  }

  // Fixed parameters - return original template
  return template
}

export const columnNullValuesResolver: TemplateResolverFn = (template: string, params: ParamTypes): string => {
  if ("constraint_fn" in params) {
    // in some cases it's possible the completeness renderers will already have $column, so check to make sure we're not duplicating
    return `${getConditionPrefix(template)} ${template.indexOf("$column") === -1 ? "$column" : ""}'s null values must not $changeDirection by more than $rawOffset percent compared to the average of the previous $range runs`
  }

  return template
}

export const columnNonNullValuesResolver: TemplateResolverFn = (template: string, params: ParamTypes): string => {
  if ("constraint_fn" in params) {
    // in some cases it's possible the completeness renderers will already have $column, so check to make sure we're not duplicating
    return `${getConditionPrefix(template)} ${template.indexOf("$column") === -1 ? "$column" : ""}'s non-null values must not $changeDirection by more than $rawOffset percent compared to the average of the previous $range runs`
  }

  return template
}

export const columnMaxToBeBetweenResolver: TemplateResolverFn = (template: string, params: ParamTypes): string => {
  if ("constraint_fn" in params) {
    const preposition = getDynamicParameterPreposition(params)
    return `${getConditionPrefix(template)} maximum value must be ${preposition} $offset of the $constraint_fn maximum of the last $range runs`
  }

  return template
}

export const columnMeanToBeBetweenResolver: TemplateResolverFn = (template: string, params: ParamTypes): string => {
  if ("constraint_fn" in params) {
    const preposition = getDynamicParameterPreposition(params)
    return `${getConditionPrefix(template)} mean value must be ${preposition} $offset of the $constraint_fn mean of the last $range runs`
  }

  return template
}

export const columnMedianToBeBetweenResolver: TemplateResolverFn = (template: string, params: ParamTypes): string => {
  if ("constraint_fn" in params) {
    const preposition = getDynamicParameterPreposition(params)
    return `${getConditionPrefix(template)} median value must be ${preposition} $offset of the $constraint_fn median of the last $range runs`
  }

  return template
}

export const columnMinToBeBetweenResolver: TemplateResolverFn = (template: string, params: ParamTypes): string => {
  if ("constraint_fn" in params) {
    const preposition = getDynamicParameterPreposition(params)
    return `${getConditionPrefix(template)} minimum value must be ${preposition} $offset of the $constraint_fn minimum of the last $range runs`
  }

  return template
}

export const columnProportionOfUniqueValuesToBeBetweenResolver: TemplateResolverFn = (
  template: string,
  params: ParamTypes,
): string => {
  if ("constraint_fn" in params) {
    return `${getConditionPrefix(template)} proportion of unique values must be $offset of the $constraint_fn of the proportions of the last $range runs`
  }

  return template
}

export const columnProportionOfNonNullValuesToBeBetweenResolver: TemplateResolverFn = (
  template: string,
  params: ParamTypes,
): string => {
  if ("constraint_fn" in params) {
    return `${getConditionPrefix(template)} proportion of non-null values must be $offset of the $constraint_fn of the proportions of the last $range runs`
  }

  return template
}

export const columnStdevToBeBetweenResolver: TemplateResolverFn = (template: string, params: ParamTypes): string => {
  if ("constraint_fn" in params) {
    return `${getConditionPrefix(template)} standard deviation must be $offset of the $constraint_fn standard deviations of the last $range runs`
  }

  return template
}

export const columnSumToBeBetweenResolver: TemplateResolverFn = (template: string, params: ParamTypes): string => {
  if ("constraint_fn" in params) {
    return `${getConditionPrefix(template)} sum must be $offset of the $constraint_fn sum of the last $range runs`
  }

  return template
}

export const columnUniqueValueCountToBeBetweenResolver: TemplateResolverFn = (
  template: string,
  params: ParamTypes,
): string => {
  if ("constraint_fn" in params) {
    return `${getConditionPrefix(template)} unique value count must be $offset of the $constraint_fn unique value counts of the last $range runs`
  }

  return template
}

export const tableColumnCountToBeBetweenResolver: TemplateResolverFn = (
  template: string,
  params: ParamTypes,
): string => {
  if ("constraint_fn" in params) {
    return `${getConditionPrefix(template)} column count must be $offset of the $constraint_fn column counts of the last $range runs`
  }

  return template
}

const templateResolvers: Record<string, TemplateResolverFn> = {
  expect_table_row_count_to_be_between: tableRowCountToBeBetweenResolver,
  expect_column_values_to_be_null: columnNullValuesResolver,
  expect_column_values_to_not_be_null: columnNonNullValuesResolver,
  expect_column_max_to_be_between: columnMaxToBeBetweenResolver,
  expect_column_mean_to_be_between: columnMeanToBeBetweenResolver,
  expect_column_median_to_be_between: columnMedianToBeBetweenResolver,
  expect_column_min_to_be_between: columnMinToBeBetweenResolver,
  expect_column_proportion_of_unique_values_to_be_between: columnProportionOfUniqueValuesToBeBetweenResolver,
  expect_column_proportion_of_non_null_values_to_be_between: columnProportionOfNonNullValuesToBeBetweenResolver,
  expect_column_stdev_to_be_between: columnStdevToBeBetweenResolver,
  expect_column_sum_to_be_between: columnSumToBeBetweenResolver,
  expect_column_unique_value_count_to_be_between: columnUniqueValueCountToBeBetweenResolver,
  expect_table_column_count_to_be_between: tableColumnCountToBeBetweenResolver,
}

export const resolveExpectationTemplate = (
  template: string | null | undefined,
  expectationType: string | undefined,
  params: ParamTypes,
) => {
  if (!template || !expectationType) return template

  const resolver = templateResolvers[expectationType]

  return resolver?.(template, params, expectationType) ?? template
}

export function getDynamicParameterPreposition(params: ParamTypes): string {
  const offsetValue = params?.offset?.value
  if (typeof offsetValue === "string" && offsetValue.includes("+/-")) return "within"
  return "between"
}
